import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOverlayContext } from 'seagull';
import { editContactModalContent } from '~/components/ContactModals/contactModals';
import { useAccountingCustomers } from '~/hooks/reactQuery/queries/useAccountingCustomers';
import { useClientContacts } from '~/hooks/reactQuery/queries/useClientContacts';
import { useIntegrationsSummary } from '~/hooks/reactQuery/queries/useIntegrationsSummary';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useContactArchiveRestoreDelete } from '~/hooks/useContactArchiveRestoreDelete';
import { selectors } from '~/store/state-selectors';
import { useStore } from '~/store/store-context';
import { Accounting } from '~/types/accounting';
import { AddContactToClientModal } from '../AddContactToClientModal/AddContactToClientModal';
import { ChangeContactRoleModal } from '../ChangeContactRoleModal/ChangeContactRoleModal';
import { buildCustomerIdToDisplay, toMappingsByContactId } from '../ContactsCard.utils';
import { useIsLastOwnerCheck } from './useIsLastOwnerCheck';
export function useContactsCard(clientId, isArchived) {
    const { contacts: allContacts, isLoading, error, refresh } = useClientContacts(clientId, {
        refreshOnHookMount: true
    });
    const { showModal } = useOverlayContext();
    const queryClient = useQueryClient();
    const contacts = useMemo(()=>isArchived ? allContacts : allContacts.filter((contact)=>contact.isActive()), [
        allContacts,
        isArchived
    ]);
    const showQbo = useStore(selectors.integration.showQboIntegration);
    const showXero = useStore(selectors.integration.showXeroIntegration);
    useIntegrationsSummary({
        enabled: showQbo || showXero,
        refreshOnHookMount: true
    });
    const { customers: qboCustomers } = useAccountingCustomers(Accounting.QBO, {
        enabled: showQbo,
        refreshOnHookMount: true
    });
    const { customers: xeroCustomers } = useAccountingCustomers(Accounting.Xero, {
        enabled: showXero,
        refreshOnHookMount: true
    });
    const mappingsByContactId = useMemo(()=>toMappingsByContactId(contacts, {
            showQbo,
            showXero,
            qboCustomerIdToDisplay: buildCustomerIdToDisplay(qboCustomers),
            xeroCustomerIdToDisplay: buildCustomerIdToDisplay(xeroCustomers)
        }), [
        contacts,
        showQbo,
        showXero,
        qboCustomers,
        xeroCustomers
    ]);
    const refreshContactsAndClients = async ()=>{
        await refresh();
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.Clients
            ]
        });
        queryClient.invalidateQueries({
            queryKey: [
                QueryKeys.ClientSummaries
            ]
        });
    };
    const { isLastOwner } = useIsLastOwnerCheck(clientId);
    const { onDelete } = useContactArchiveRestoreDelete({
        onSuccess: refreshContactsAndClients,
        isLastOwner
    });
    const onAddContact = ()=>showModal({
            biName: 'client_detail_add_contact',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(AddContactToClientModal, {
                    clientId: clientId,
                    dismissModal: dismissModal
                });
            }
        });
    const onEditContact = async (contact)=>{
        await showModal({
            biName: 'client_detail_edit_contact',
            content: editContactModalContent(clientId, contact, true)
        });
        refresh();
    };
    const onChangeRole = (contact)=>showModal({
            biName: 'client_detail_change_contact_role',
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ChangeContactRoleModal, {
                    clientId: clientId,
                    contact: contact,
                    dismissModal: dismissModal
                });
            }
        });
    const isError = error != null;
    const isSettled = !isLoading && !isError;
    return {
        contacts,
        isLoading,
        isError,
        isEmpty: isSettled && contacts.length === 0,
        hasContacts: isSettled && contacts.length > 0,
        onAddContact,
        onEditContact,
        onChangeRole,
        mappingsByContactId,
        onDeleteContact: onDelete
    };
}
